(() => {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer || !window.fetch || !window.FormData) return;
  const root = drawer.dataset.rootUrl.endsWith('/') ? drawer.dataset.rootUrl : drawer.dataset.rootUrl + '/';
  const productForms = '.pcard-form, .commerce-product-form';
  let pending = false;
  let opener;
  let refreshRequest;
  let mutationRevision = 0;
  let addQueue = Promise.resolve();
  const pendingAdds = [];
  let cartItems = JSON.parse(drawer.querySelector('[data-cart-items]').textContent);

  function matchingItem(form) {
    const data = new FormData(form);
    const properties = Array.from(data.entries()).filter(([name, value]) => name.startsWith('properties[') && String(value) !== '')
      .map(([name, value]) => [name.slice(11, -1), String(value)]).sort(([a], [b]) => a.localeCompare(b));
    return [...cartItems, ...pendingAdds.map(operation => operation.item)].find(item => String(item.variant_id) === String(data.get('id')) &&
      String(item.selling_plan_allocation?.selling_plan?.id || '') === String(data.get('selling_plan') || '') &&
      JSON.stringify(Object.entries(item.properties || {}).filter(([, value]) => String(value) !== '').map(([name, value]) => [name, String(value)]).sort(([a], [b]) => a.localeCompare(b))) === JSON.stringify(properties));
  }

  function syncProductControls() {
    document.querySelectorAll(productForms).forEach(form => {
      const item = matchingItem(form);
      const buttons = Array.from(form.elements).filter(button => button.matches('[data-add-to-cart], button.pc-add'));
      buttons.forEach(button => {
        let action = button.closest('.product-cart-action');
        if (!action && !item) return;
        if (!action) {
          action = document.createElement('div');
          action.className = 'product-cart-action';
          action.style.setProperty('--product-cart-height', (button.getBoundingClientRect().height || parseFloat(getComputedStyle(button).minHeight) || 44) + 'px');
          button.before(action);
          action.append(button, drawer.querySelector('[data-product-cart-quantity]').content.cloneNode(true));
        }
        const control = action.querySelector('[data-cart-quantity]');
        button.hidden = Boolean(item);
        control.hidden = !item;
        if (!item) return;
        control.dataset.cartKey = item.key;
        control.dataset.quantity = item.quantity;
        control.querySelector('input').value = item.quantity;
        control.querySelector('input').setAttribute('aria-label', control.dataset.quantityLabel.replace('__TITLE__', item.product_title));
        control.querySelector('[data-cart-step="-1"]').setAttribute('aria-label', control.dataset.decreaseLabel.replace('__TITLE__', item.product_title));
        control.querySelector('[data-cart-step="1"]').setAttribute('aria-label', control.dataset.increaseLabel.replace('__TITLE__', item.product_title));
        control.querySelectorAll('button, input').forEach(input => {
          input.disabled = pending || Boolean(form.closest('[data-shopping-busy]'));
          if (pending) input.dataset.cartBusy = 'true';
        });
      });
    });
  }

  function sectionIds() {
    return [drawer.dataset.sectionId, ...Array.from(document.querySelectorAll('[data-cart-section]'), node => node.dataset.cartSection)];
  }

  function status(message, error = false, form) {
    const live = document.querySelector('[data-cart-announcement]');
    if (live) live.textContent = message;
    const drawerStatus = drawer.querySelector('[data-cart-status]');
    drawerStatus.textContent = message;
    drawerStatus.dataset.error = String(error);
    const host = form || (!drawer.open && document.querySelector('[data-cart-section]'));
    if (host) {
      let inline = host.querySelector('.cart-inline-status');
      if (!inline) {
        inline = document.createElement('p');
        inline.className = 'cart-inline-status';
        inline.setAttribute('role', 'status');
        host.append(inline);
      }
      inline.textContent = message;
      inline.dataset.error = String(error);
    }
  }

  function busy(value) {
    value = value || pendingAdds.length > 0;
    pending = value;
    document.querySelectorAll(productForms).forEach(form => form.setAttribute('aria-busy', String(value && (pendingAdds.length === 0 || pendingAdds.some(operation => operation.form === form)))));
    drawer.querySelector('[data-cart-drawer-content]').setAttribute('aria-busy', String(value));
    if (value) {
      const controls = [...document.querySelectorAll('[data-cart-quantity] button, [data-cart-quantity] input, [data-cart-remove], #CartDrawerCheckout button, [data-cart-section] [name="checkout"], [data-cart-section] [name="update"]')];
      document.querySelectorAll(productForms).forEach(form => controls.push(...Array.from(form.elements).filter(control => control.type === 'submit' && (pendingAdds.length === 0 || pendingAdds.some(operation => operation.form === form) || control.matches('[data-buy-now], .commerce-buy-button')))));
      controls.forEach(control => {
        if ('disabled' in control && !control.disabled) {
          control.dataset.cartBusy = 'true';
          control.disabled = true;
        }
      });
    } else {
      document.querySelectorAll('[data-cart-busy]').forEach(control => {
        control.disabled = Boolean(control.closest('[data-shopping-busy]'));
        delete control.dataset.cartBusy;
      });
    }
  }

  function open(trigger) {
    if (!drawer.open) {
      opener = trigger || document.activeElement;
      drawer.showModal();
      document.body.classList.add('cart-drawer-open');
    }
  }

  async function jsonRequest(url, options = {}) {
    const response = await fetch(url, { credentials: 'same-origin', ...options, headers: { Accept: 'application/json', ...options.headers } });
    const result = await response.json();
    if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : drawer.dataset.errorText);
    return result;
  }

  function renderSections(sections) {
    const parsed = new Map();
    for (const id of sectionIds()) {
      if (typeof sections?.[id] !== 'string') return false;
      parsed.set(id, new DOMParser().parseFromString(sections[id], 'text/html'));
    }
    const content = parsed.get(drawer.dataset.sectionId).querySelector('[data-cart-drawer-content]');
    if (!content) return false;
    for (const current of document.querySelectorAll('[data-cart-section]')) {
      const replacement = parsed.get(current.dataset.cartSection).querySelector('[data-cart-section]');
      if (!replacement) return false;
    }
    drawer.querySelector('[data-cart-drawer-content]').replaceWith(content);
    cartItems = JSON.parse(content.querySelector('[data-cart-items]').textContent);
    syncProductControls();
    document.querySelectorAll('[data-cart-count]').forEach(count => {
      count.textContent = content.dataset.itemCount;
      count.hidden = Number(content.dataset.itemCount) === 0;
    });
    document.querySelectorAll('[data-cart-section]').forEach(current => {
      const note = current.querySelector('[name="note"]')?.value;
      const replacement = parsed.get(current.dataset.cartSection).querySelector('[data-cart-section]');
      current.replaceWith(replacement);
      if (note != null && replacement.querySelector('[name="note"]')) replacement.querySelector('[name="note"]').value = note;
    });
    return true;
  }

  async function refresh() {
    const revision = mutationRevision;
    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionIds().join(','));
    const sections = await jsonRequest(url.toString());
    if (revision !== mutationRevision) return;
    if (!renderSections(sections)) throw new Error(drawer.dataset.errorText);
  }

  async function reconcile(sections, revision = mutationRevision) {
    if (revision !== mutationRevision) return;
    if (renderSections(sections)) return;
    await refresh();
  }

  async function add(form, trigger) {
    if (pendingAdds.some(operation => operation.form === form)) return;
    const data = new FormData(form);
    data.delete('return_to');
    data.set('sections', sectionIds().join(','));
    data.set('sections_url', window.location.pathname + window.location.search);
    const operation = {
      form,
      item: {
        variant_id: Number(data.get('id')),
        key: 'pending-' + pendingAdds.length + '-' + data.get('id'),
        quantity: Math.max(1, Number(data.get('quantity')) || 1),
        product_title: form.closest('.pcard')?.querySelector('h3')?.textContent.trim() || form.closest('.commerce-product')?.querySelector('h1')?.textContent.trim() || '',
        properties: Object.fromEntries(Array.from(data.entries()).filter(([name, value]) => name.startsWith('properties[') && String(value) !== '').map(([name, value]) => [name.slice(11, -1), String(value)])),
        selling_plan_allocation: data.get('selling_plan') ? { selling_plan: { id: data.get('selling_plan') } } : null
      }
    };
    pendingAdds.push(operation);
    busy(true);
    status('', false, form);
    syncProductControls();
    addQueue = addQueue.catch(() => {}).then(async () => {
      const openAfterAdd = cartItems.length === 0;
      const revision = ++mutationRevision;
      try {
        const result = await jsonRequest(root + 'cart/add.js', { method: 'POST', body: data });
        const item = result.items?.[0] || result;
        if (item.key) {
          cartItems = cartItems.filter(current => current.key !== item.key);
          cartItems.push(item);
        }
        pendingAdds.splice(pendingAdds.indexOf(operation), 1);
        syncProductControls();
        if (openAfterAdd) open(trigger);
        reconcile(result.sections, revision).catch(() => {});
      } catch (error) {
        pendingAdds.splice(pendingAdds.indexOf(operation), 1);
        syncProductControls();
        status(error.message || drawer.dataset.errorText, true, form);
      } finally {
        busy(false);
      }
    });
    await addQueue;
  }

  async function change(key, quantity, trigger) {
    if (pending || !Number.isInteger(quantity) || quantity < 0) return;
    const step = trigger?.dataset.cartStep;
    const focusInput = trigger?.matches('[data-cart-quantity-input]');
    const action = trigger?.closest('.product-cart-action');
    const scope = trigger?.closest('[data-cart-drawer]') ? drawer : document;
    busy(true);
    status('');
    const previousItems = cartItems;
    cartItems = cartItems.flatMap(item => item.key !== key ? [item] : quantity ? [{ ...item, quantity }] : []);
    syncProductControls();
    document.querySelectorAll('[data-cart-quantity]').forEach(control => {
      if (control.dataset.cartKey === key) control.querySelector('input').value = quantity;
    });
    let updated = false;
    try {
      const revision = ++mutationRevision;
      const result = await jsonRequest(root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: sectionIds(), sections_url: window.location.pathname + window.location.search })
      });
      updated = true;
      cartItems = result.items;
      syncProductControls();
      reconcile(result.sections, revision).catch(() => {});
      status(drawer.dataset.updatedText);
    } catch (error) {
      if (!updated) {
        cartItems = previousItems;
        syncProductControls();
        document.querySelectorAll('[data-cart-quantity]').forEach(control => control.querySelector('input').value = control.dataset.quantity);
      }
      status(updated ? '' : error.message || drawer.dataset.errorText, !updated);
    } finally {
      busy(false);
      const control = action ? action.querySelector('[data-cart-quantity]:not([hidden])') : Array.from(scope.querySelectorAll('[data-cart-quantity]')).find(control => control.dataset.cartKey === key);
      const target = control && (focusInput ? control.querySelector('input') : Array.from(control.querySelectorAll('[data-cart-step]')).find(button => button.dataset.cartStep === step));
      if (target) target.focus();
      else if (action) action.querySelector('button:not([hidden])')?.focus();
      else if (drawer.open) drawer.querySelector('[data-cart-close]').focus();
    }
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    if (form.matches('.commerce-cart-form') && event.submitter?.name === 'update') {
      event.preventDefault();
      if (pending) return;
      busy(true);
      status('');
      const revision = ++mutationRevision;
      const updates = Object.fromEntries(Array.from(form.querySelectorAll('[data-cart-quantity]'), control => [control.dataset.cartKey, Number(control.querySelector('input').value)]));
      jsonRequest(root + 'cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates, note: new FormData(form).get('note') || '', sections: sectionIds(), sections_url: window.location.pathname + window.location.search })
      }).then(result => {
        cartItems = result.items;
        syncProductControls();
        reconcile(result.sections, revision).catch(() => {});
        status(drawer.dataset.updatedText);
      }).catch(error => status(error.message || drawer.dataset.errorText, true)).finally(() => busy(false));
      return;
    }
    if (!form.matches(productForms) || event.submitter?.matches('[data-buy-now], .commerce-buy-button, .shopify-payment-button__button')) return;
    event.preventDefault();
    if (!pending || pendingAdds.length > 0) add(form, event.submitter || document.activeElement);
  });

  document.addEventListener('click', event => {
    const close = event.target.closest('[data-cart-close]');
    if (close) drawer.close();
    const cartLink = event.target.closest('[data-cart-open]');
    if (cartLink && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
      event.preventDefault();
      open(cartLink);
      if (!pending && !refreshRequest) {
        status('');
        refreshRequest = refresh().catch(() => status('')).finally(() => { refreshRequest = null; });
      }
    }
    const step = event.target.closest('[data-cart-step]');
    if (step) {
      event.preventDefault();
      const control = step.closest('[data-cart-quantity]');
      change(control.dataset.cartKey, Math.max(0, Number(control.querySelector('input').value) + Number(step.dataset.cartStep)), step);
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      change(remove.dataset.cartRemove, 0, remove);
    }
  });

  document.addEventListener('change', event => {
    if (event.target.name?.startsWith('properties[') || event.target.name === 'selling_plan' || event.target.name === 'id') syncProductControls();
    if (!event.target.matches('[data-cart-quantity-input]')) return;
    const quantity = Number(event.target.value);
    const control = event.target.closest('[data-cart-quantity]');
    if (!Number.isInteger(quantity) || quantity < 0 || event.target.value.trim() === '') {
      event.target.value = control.dataset.quantity;
      return;
    }
    change(control.dataset.cartKey, quantity, event.target);
  });

  drawer.addEventListener('click', event => {
    if (event.target !== drawer) return;
    const rect = drawer.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) drawer.close();
  });
  drawer.addEventListener('close', () => {
    document.body.classList.remove('cart-drawer-open');
    if (opener?.isConnected) {
      const target = opener.hidden ? opener.closest('.product-cart-action')?.querySelector('[data-cart-step="1"]') : opener;
      target?.focus();
    }
  });
  syncProductControls();
  new MutationObserver(records => {
    if (records.some(record => Array.from(record.addedNodes).some(node => node.nodeType === 1 && (node.matches(productForms) || node.querySelector(productForms))))) syncProductControls();
  }).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
})();
